import { Link, Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Input, Text } from '@/components/ui';
import { isValidEmail } from '@console/utils';
import { useAuth, useAuthStore } from '@/lib/auth';
import { LogInIcon } from 'lucide-react';
import { Logo } from '@/assets/images/Logo';
import { toast } from 'sonner';

const createLoginSchema = (t: any) =>
  z.object({
    email: z.string().email(t('auth.invalidEmail')).refine(isValidEmail, t('auth.invalidEmail')),
    password: z.string().min(6, t('auth.passwordMinLength')),
  });

type LoginForm = z.infer<ReturnType<typeof createLoginSchema>>;

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  const loginSchema = createLoginSchema(t);

  const from = location.state?.from?.pathname || '/';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(data: LoginForm) {
    try {
      await login(data.email, data.password);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('auth.loginError'));
    }
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="bg-base-white border-transparent-dark-12 p-lg flex w-96 flex-col rounded-3xl border shadow-xs">
        <Logo className="self-center" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-2xl flex flex-col" noValidate>
          <Input
            {...register('email')}
            type="email"
            label={t('auth.email')}
            placeholder={t('auth.emailPlaceholder')}
            error={errors.email?.message as string}
            disabled={isLoading}
          />
          <Input
            {...register('password')}
            type="password"
            label={t('auth.password')}
            placeholder={t('auth.passwordPlaceholder')}
            className="mt-md"
            error={errors.password?.message}
            disabled={isLoading}
          />
          <Link to="/forgot-password" className="self-end">
            <Text
              variant="label"
              strong
              className="text-neutral-muted mt-2xs hover:text-neutral-default cursor-pointer transition-colors duration-150 ease-in-out"
              role="button"
            >
              {t('auth.forgotPassword')}
            </Text>
          </Link>

          <Button type="submit" className="mt-2xl" loading={isLoading}>
            {t('auth.login')} <LogInIcon size={20} />
          </Button>
        </form>
      </div>
    </div>
  );
}
